/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents } from '@gh/builder-shared-data';
import { ActionFlow, DateTimeTypes, FormStateBuilder, getRouteInitData } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import { buildDateTimeColumn, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { currentYear } from '@gh/core-util';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { SchedulingCalendarDetailsBusinessService } from '../../resources/scheduling-calendar-details-business.service';
import {
  CalendarDetails,
  CalendarListItem,
  calendarToDto,
} from '../../resources/scheduling-calendar-details.model';
import { SchedulingCalendarDetailsFormState } from './scheduling-calendar-details-form.service';

type CalendarDetailsSaveFlowState = FormFlowState<CalendarDetails> & { saveResponse: CalendarListItem[] };

@Component({
  standalone: false,
  selector: 'gh-scheduling-calendar-details-tab',
  styleUrls: ['scheduling-calendar-details-tab.component.scss'],
  templateUrl: 'scheduling-calendar-details-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SchedulingCalendarDetailsFormState],
})
export class SchedulingCalendarDetailsComponent extends AbstractFormComponent<CalendarDetails>
  implements OnInit, AfterViewInit {

  gridDefinition: GridDefinition;

  @ViewChild(MatCalendar, { static: false }) calendar: MatCalendar<Date>;

  get isProcessEnabled(): boolean {
    return !!calendarToDto({ dates: this.form.dirtyRecords }).dates.length;
  }

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private waitings: WaitingService,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private userContext: BuilderUserContext,
              private navigations: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private formStateBuilder: FormStateBuilder,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              public form: SchedulingCalendarDetailsFormState,
              private business: SchedulingCalendarDetailsBusinessService) {
    super();
  }

  get completeFlow(): ActionFlow<CalendarDetailsSaveFlowState> {
    return this.pipelines.complete<CalendarDetailsSaveFlowState>({
      execute: this.commonActions.async((state) =>
        this.business.update(this.form.dirtyRecords).pipe(
          switchMap$(() => this.business.get(this.form.visibleYear)),
          map$((saveResponse) => ({ ...state, saveResponse })))),
      finish: ({ saveResponse }) => {
        this.form.build(this.form.visibleYear, saveResponse);
      },
    });
  }

  ngOnInit(): void {
    const { calendar } = getRouteInitData(this.route);
    this.form.build(currentYear(), calendar);

    this.gridDefinition = this.buildGridDefinition();
  }

  ngAfterViewInit(): void {
    this.calendar.stateChanges.subscribe(() => {
      const date = this.calendar.activeDate;
      const year = date.getFullYear();
      if (!this.form.isYearPreloaded(year)) {
        this.business.get(year).pipe(
          (this.waitings.operator()))
          .subscribe((records) => {
            this.form.loadRecords(year, records);
            this.form.setVisibleDate(date);
          });
      } else {
        this.form.setVisibleDate(date);
      }
    });
  }

  onAdd(): void {
    if (this.form.isSelectedDateExist) {
      this.notifications.error('msg_sch_date_exist');
    } else {
      this.form.addRecord();
    }
  }

  onDelete(record: CalendarListItem): void {
    this.dialogs.confirmation(yesCancelConfirmation('msg_calendar_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.deleteRecord(record));
  }

  onSelectedDateChanged(date: Date): void {
    this.form.setSelectedDate(date);
  }

  onCurrentDescriptionChange(description: string): void {
    this.form.setCurrentDescription(description);
  }

  onReset(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.get(this.form.visibleYear).pipe((this.waitings.operator()))))
      .subscribe((items) => {
        this.form.build(this.form.visibleYear, items);
      });
  }

  goToAel(): void {
    this.navigations.goToAel({
      label: 'lbl_menu_calendar',
      entityType: 'OFFICE',
      entityId: this.userContext.office!.id,
      eventTypeIds: [AelEvents.Office.EditedCalendar],
    });
  }

  private buildGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual([{
      ...buildDateTimeColumn(DateTimeTypes.MediumDateAndWeekday),
      field: 'date',
      headerName: this.translations.get('lbl_date'),
      width: 200,
      suppressSizeToFit: true,
    }, {
      field: 'description',
      headerName: this.translations.get('lbl_description'),
      width: 200,
    }]);
  }
}
